import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Collapse,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ReactMarkdown, { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { MouseEvent, CSSProperties, useState } from "react";
import { stripMarkdownAndHTML } from "../../../../shared/utils/formatter";
import "./style.css";

interface InsightAccordionProps {
  title: string;
  text: string;
  summary?: string;
  defaultExpanded?: boolean;
  isLoading?: boolean;
  isDone?: boolean;
}

const markdownComponents: Components = {
  h1: ({ children }) => (
    <Typography
      variant="h2"
      component="h1"
      sx={{ fontWeight: "bold", mt: 1.5, mb: 0.5, color: "primary.dark" }}
    >
      {children}
    </Typography>
  ),
  h2: ({ children }) => (
    <Typography
      variant="h4"
      component="h2"
      sx={{ fontWeight: 600, mt: 1.5, mb: 1.5, color: "text.primary" }}
    >
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography
      variant="h6"
      component="h3"
      sx={{ fontWeight: "bold", mt: 1, mb: 1, color: "primary.main" }}
    >
      {children}
    </Typography>
  ),
  h4: ({ children }) => (
    <Typography
      variant="subtitle1"
      component="h4"
      sx={{ fontWeight: "bold", mt: 1.5, mb: 1 }}
    >
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography
      variant="body2"
      component="p"
      color="text.primary"
      sx={{ mb: 1.5, lineHeight: 1.6 }}
    >
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: 6, marginLeft: 20 }}>
      <Typography variant="body2" component="span" color="text.primary">
        {children}
      </Typography>
    </li>
  ),
  span: ({ children, style }: any) => {
    const inlineStyle: CSSProperties = style as CSSProperties;
    return <span style={inlineStyle}>{children}</span>;
  },
  hr: () => <Box sx={{ my: 2, borderBottom: "1px dashed lightgray" }} />,
};

const summaryComponents: Components = {
  ...markdownComponents,
  p: ({ children }) => (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ mt: 0.5, fontStyle: "italic" }}
    >
      {children}
    </Typography>
  ),
  li: ({ children }) => (
    <li style={{ marginBottom: 2, marginLeft: 16 }}>
      <Typography variant="body2" component="span" color="text.secondary">
        {children}
      </Typography>
    </li>
  ),
};

const InsightAccordion = ({ title, text, summary, defaultExpanded, isLoading, isDone }: InsightAccordionProps) => {
  const [copied, setCopied] = useState(false);

  const fallbackCopy = (value: string) => {
    const textarea = document.createElement("textarea");
    textarea.value = value;
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    textarea.style.opacity = "0";
    document.body.appendChild(textarea);

    textarea.focus();
    textarea.select();

    document.execCommand("copy");
    document.body.removeChild(textarea);
  };

  const handleCopy = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    const cleanTitle = stripMarkdownAndHTML(title);
    const cleanSummary = stripMarkdownAndHTML(summary || "");
    const cleanText = stripMarkdownAndHTML(text);

    const combinedText = `${cleanTitle}\n\n${
      cleanSummary ? cleanSummary + "\n\n" : ""
    }${cleanText}`;

    fallbackCopy(combinedText);

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Accordion className="insightAccordion" defaultExpanded={defaultExpanded}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: "primary.main" }} />}
        aria-controls="panel-content"
        id="panel-header"
        sx={{
          px: 3,
          py: 1.5,
          "&.Mui-expanded": { minHeight: 64 },
          borderBottom: "1px solid rgba(0, 0, 0, .125)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "text.primary" }}
              >
                {title}
              </Typography>

              {isDone && (
                <Tooltip title={copied ? "Copied!" : "Copy summary & text"} arrow>
                  <IconButton onClick={handleCopy} size="small">
                    {copied ? (
                      <CheckCircleOutlineIcon fontSize="small" color="primary" />
                    ) : (
                      <ContentCopyIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Collapse in={!!summary} timeout={800}>
              <Box sx={{ mt: summary ? 1 : 0 }}>
                {summary && (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                    components={summaryComponents}
                  >
                    {summary}
                  </ReactMarkdown>
                )}
              </Box>
            </Collapse>
            
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 3, pb: 3, bgcolor: "#fbfbfb" }}>
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={32} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {text}
            </Typography>
          </Box>
        ) : (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {text}
          </ReactMarkdown>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default InsightAccordion;